
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Building2,
  Users,
  FileText,
  Package,
  CreditCard,
  BarChart2,
  TrendingUp,
  Calendar,
} from "lucide-react";
import { analyticsAPI, companyAPI } from "@/services/api";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell } from "recharts";

const MasterDashboard = () => {
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalCandidates: 0,
    activeExams: 0,
    totalRevenue: 0,
    questionBanksSold: 0,
  });
  
  const [revenueData, setRevenueData] = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        // const response = await analyticsAPI.getDashboardStats();
        
        setStats({
          totalCompanies: response.data.totalCompanies || 0,
          activeCompanies: response.data.activeCompanies || 0,
          totalCandidates: response.data.totalCandidates || 0,
          activeExams: response.data.activeExams || 0,
          totalRevenue: response.data.totalRevenue || 0,
          questionBanksSold: response.data.questionBanksSold || 0,
        });
        
        setRevenueData(response.data.revenueData || getDefaultRevenueData());
        setCompanyData(response.data.companyData || getDefaultCompanyData());
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error("Failed to load dashboard data");
        // Load default data for display
        setStats({
          totalCompanies: 12,
          activeCompanies: 8,
          totalCandidates: 584,
          activeExams: 47,
          totalRevenue: 24500,
          questionBanksSold: 35,
        });
        setRevenueData(getDefaultRevenueData());
        setCompanyData(getDefaultCompanyData());
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getDefaultRevenueData = () => [
    { month: 'Jan', amount: 3200 },
    { month: 'Feb', amount: 3800 },
    { month: 'Mar', amount: 4200 },
    { month: 'Apr', amount: 3900 },
    { month: 'May', amount: 4600 },
    { month: 'Jun', amount: 4800 },
  ];

  const getDefaultCompanyData = () => [
    { name: 'Basic Plan', value: 30 },
    { name: 'Pro Plan', value: 45 },
    { name: 'Enterprise', value: 25 },
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  const StatCard = ({ title, value, icon: Icon, description }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Master Dashboard</h1>
        <p className="text-muted-foreground">
          Overview of platform performance and statistics.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Total Companies"
          value={stats.totalCompanies}
          icon={Building2}
          description="Total registered companies on the platform"
        />
        <StatCard
          title="Active Companies"
          value={stats.activeCompanies}
          icon={Building2}
          description="Companies with active subscriptions"
        />
        <StatCard
          title="Total Candidates"
          value={stats.totalCandidates}
          icon={Users}
          description="Total number of candidates registered"
        />
        <StatCard
          title="Active Exams"
          value={stats.activeExams}
          icon={FileText}
          description="Exams currently in progress"
        />
        <StatCard
          title="Total Revenue"
          value={`$${stats.totalRevenue.toLocaleString()}`}
          icon={CreditCard}
          description="Total platform revenue"
        />
        <StatCard
          title="Question Banks Sold"
          value={stats.questionBanksSold}
          icon={Package}
          description="Total question bank packs sold"
        />
      </div>

      <Tabs defaultValue="revenue" className="space-y-4">
        <TabsList>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="companies">Companies</TabsTrigger>
        </TabsList>
        <TabsContent value="revenue" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Monthly Revenue</CardTitle>
              <CardDescription>
                Revenue generated over the last 6 months
              </CardDescription>
            </CardHeader>
            <CardContent className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${value}`} />
                  <Legend />
                  <Bar dataKey="amount" fill="#0ea5e9" name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="companies" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Companies by Plan</CardTitle>
              <CardDescription>
                Distribution of companies across subscription plans
              </CardDescription>
            </CardHeader>
            <CardContent className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={companyData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {companyData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => value} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MasterDashboard;
